import { formatCurrency } from "@certn/utils";
import { t } from "i18next";
import { number, object, string } from "yup";

import type { Schemas } from "../../types";
import { formatCheckPriceLabel } from "../../utils/formatCheckPriceLabel";
import {
  AUSTRALIAN_CRIMINAL_OPTION,
  CRIMINAL_RECORD_VALIDATION,
  US_CRIMINAL_PREFERENCE,
  type AustralianCriminalOption,
} from "./constants";
import type {
  UsCriminalCheckArguments,
  UsCriminalChecks,
  UsCriminalPreferenceEnum,
} from "./types";

export type AustralianCriminalCheckArguments = {
  australian_criminal_preference: Schemas["AustralianCriminalPreferenceEnum"];
  australian_ncchc_check_type: Schemas["AustralianNcchcCheckTypeEnum"];
};

export type UsCriminalPreferenceFields = Pick<
  Schemas["CriminalCheckArgumentsCreate"],
  "us_criminal_preference" | "us_criminal_checks"
>;

export const US_CRIMINAL_CHECK_TYPE = {
  SSN: "US_SSN_TRACE_1",
  NCD: "US_NATIONAL_CRIMINAL_DATABASE_1",
  NSOR: "US_NATIONAL_SEX_OFFENDER_REGISTRY_1",
  GWL: "GLOBAL_WATCHLIST_AND_SANCTIONS_1",
  FEDERAL: "US_FEDERAL_CRIMINAL_COURT_RECORD_CHECK_1",
  COUNTY: "US_COUNTY_CRIMINAL_COURT_RECORD_CHECK_1",
} as const;

export type UsCriminalCourtCheckType =
  | typeof US_CRIMINAL_CHECK_TYPE.FEDERAL
  | typeof US_CRIMINAL_CHECK_TYPE.COUNTY;

export const US_CRIMINAL_ORDERING_TYPE = {
  CURRENT_ADDRESS: "CURRENT_ADDRESS",
  ADDRESS_HISTORY: "ADDRESS_HISTORY",
} as const;

export const US_CRIMINAL_CUSTOM_CHECKBOX = {
  SSN: "ssn",
  NCD: "ncd",
  NSOR: "nsor",
  GWL: "gwl",
  FEDERAL_CURRENT: "federal_current",
  COUNTY_CURRENT: "county_current",
  FEDERAL_SEVEN_YEAR: "federal_seven_year",
  COUNTY_SEVEN_YEAR: "county_seven_year",
} as const;

export type UsCriminalCustomCheckbox =
  (typeof US_CRIMINAL_CUSTOM_CHECKBOX)[keyof typeof US_CRIMINAL_CUSTOM_CHECKBOX];

const CURRENT_ADDRESS_COURT_ARGUMENTS: UsCriminalCheckArguments = {
  ordering_type: US_CRIMINAL_ORDERING_TYPE.CURRENT_ADDRESS,
};

const SEVEN_YEAR_COURT_ARGUMENTS: UsCriminalCheckArguments = {
  ordering_type: US_CRIMINAL_ORDERING_TYPE.ADDRESS_HISTORY,
  us_criminal_database_search_years: 7,
};

export type UsCriminalTierPreference = Extract<
  UsCriminalPreferenceEnum,
  "TIER_1" | "TIER_2" | "TIER_3"
>;

const US_CRIMINAL_TIER_1_CHECKS: UsCriminalChecks = {
  [US_CRIMINAL_CHECK_TYPE.SSN]: {},
  [US_CRIMINAL_CHECK_TYPE.NCD]: {},
  [US_CRIMINAL_CHECK_TYPE.NSOR]: {},
  [US_CRIMINAL_CHECK_TYPE.GWL]: {},
};

export const US_CRIMINAL_TIER_CHECKS: Record<
  UsCriminalTierPreference,
  UsCriminalChecks
> = {
  TIER_1: US_CRIMINAL_TIER_1_CHECKS,
  TIER_2: {
    ...US_CRIMINAL_TIER_1_CHECKS,
    [US_CRIMINAL_CHECK_TYPE.FEDERAL]: { ...CURRENT_ADDRESS_COURT_ARGUMENTS },
    [US_CRIMINAL_CHECK_TYPE.COUNTY]: { ...CURRENT_ADDRESS_COURT_ARGUMENTS },
  },
  TIER_3: {
    ...US_CRIMINAL_TIER_1_CHECKS,
    [US_CRIMINAL_CHECK_TYPE.FEDERAL]: { ...SEVEN_YEAR_COURT_ARGUMENTS },
    [US_CRIMINAL_CHECK_TYPE.COUNTY]: { ...SEVEN_YEAR_COURT_ARGUMENTS },
  },
};

export const US_CRIMINAL_PASS_THROUGH_CHECK_TYPES = new Set<string>([
  US_CRIMINAL_CHECK_TYPE.NCD,
  US_CRIMINAL_CHECK_TYPE.COUNTY,
  US_CRIMINAL_CHECK_TYPE.FEDERAL,
]);

const US_CRIMINAL_CHECK_TYPE_SET = new Set<string>(
  Object.values(US_CRIMINAL_CHECK_TYPE)
);

export type UsCriminalCatalogPrice = {
  price: number | null;
  currency: string;
  priceIsCatalogMinimum: boolean;
};

type UsCriminalCatalogCheckFields = Pick<
  Schemas["CustomerAccountCheckList"] | Schemas["AccountCheckListChild"],
  "type" | "price" | "currency" | "price_is_catalog_minimum"
>;

export type UsCriminalCatalogPriceSource = UsCriminalCatalogCheckFields & {
  children?: readonly UsCriminalCatalogCheckFields[] | null;
};

function considerUsCriminalCatalogCheck(
  index: Map<string, UsCriminalCatalogPrice>,
  check: UsCriminalCatalogPriceSource
): void {
  if (
    !US_CRIMINAL_CHECK_TYPE_SET.has(check.type) ||
    index.has(check.type) ||
    !check.currency
  ) {
    return;
  }

  index.set(check.type, {
    price: check.price ?? null,
    currency: check.currency,
    priceIsCatalogMinimum: check.price_is_catalog_minimum ?? false,
  });
}

export function indexUsCriminalCatalogPrices(
  checks: readonly UsCriminalCatalogPriceSource[] | null | undefined
): Map<string, UsCriminalCatalogPrice> {
  const index = new Map<string, UsCriminalCatalogPrice>();
  if (!checks) {
    return index;
  }

  for (const check of checks) {
    considerUsCriminalCatalogCheck(index, check);
    for (const child of check.children ?? []) {
      considerUsCriminalCatalogCheck(index, child);
    }
  }

  return index;
}

export function sumUsCriminalTierCatalogPrice(
  checks: UsCriminalChecks,
  catalog: ReadonlyMap<string, UsCriminalCatalogPrice>
): {
  amount: number | null;
  currency: string | null;
  hasPassThrough: boolean;
} {
  let amount = 0;
  let currency: string | null = null;
  let hasPassThrough = false;
  let isMissingPrice = false;

  for (const checkType of Object.keys(checks)) {
    if (US_CRIMINAL_PASS_THROUGH_CHECK_TYPES.has(checkType)) {
      hasPassThrough = true;
    }

    const entry = catalog.get(checkType);
    if (!entry || entry.price == null) {
      isMissingPrice = true;
      continue;
    }

    if (currency == null) {
      currency = entry.currency;
    }
    amount += entry.price;
  }

  return {
    amount: isMissingPrice ? null : amount,
    currency,
    hasPassThrough,
  };
}

export function formatUsCriminalOptionLabel({
  label,
  price,
  currency,
  priceIsCatalogMinimum = false,
  hasPassThrough = false,
  locale = "en-US",
  fromPrefix = "From ",
}: {
  label: string;
  price?: number | null;
  currency?: string | null;
  priceIsCatalogMinimum?: boolean;
  hasPassThrough?: boolean;
  locale?: string;
  fromPrefix?: string;
}): string {
  const formatted = formatCheckPriceLabel(price, {
    currency,
    locale,
    priceIsCatalogMinimum,
    fromPrefix,
  });
  const pricePart = formatted ? ` – ${formatted}` : "";
  const asterisk = hasPassThrough ? "*" : "";
  return `${label}${pricePart}${asterisk}`;
}

export function formatUsCriminalCatalogCheckLabel(
  label: string,
  checkType: string,
  catalog: ReadonlyMap<string, UsCriminalCatalogPrice>,
  fromPrefix: string
): string {
  const entry = catalog.get(checkType);
  return formatUsCriminalOptionLabel({
    label,
    price: entry?.price,
    currency: entry?.currency,
    priceIsCatalogMinimum: entry?.priceIsCatalogMinimum ?? false,
    hasPassThrough: US_CRIMINAL_PASS_THROUGH_CHECK_TYPES.has(checkType),
    fromPrefix,
  });
}

export function formatUsCriminalTierOptionLabel(
  label: string,
  checks: UsCriminalChecks,
  catalog: ReadonlyMap<string, UsCriminalCatalogPrice>,
  fromPrefix: string
): string {
  const { amount, currency, hasPassThrough } = sumUsCriminalTierCatalogPrice(
    checks,
    catalog
  );
  return formatUsCriminalOptionLabel({
    label,
    price: amount,
    currency,
    hasPassThrough,
    fromPrefix,
  });
}

export function cloneUsCriminalChecks(
  checks: UsCriminalChecks | null | undefined
): UsCriminalChecks {
  if (checks == null) {
    return {};
  }

  const next: UsCriminalChecks = {};
  for (const [key, value] of Object.entries(checks)) {
    next[key] = { ...(value ?? {}) };
  }
  return next;
}

export function isCurrentAddressCourt(
  args?: UsCriminalCheckArguments
): boolean {
  return args?.ordering_type === US_CRIMINAL_ORDERING_TYPE.CURRENT_ADDRESS;
}

export function isSevenYearCourt(args?: UsCriminalCheckArguments): boolean {
  if (args?.ordering_type === US_CRIMINAL_ORDERING_TYPE.ADDRESS_HISTORY) {
    return true;
  }
  return (
    args != null &&
    "us_criminal_database_search_years" in args &&
    args.ordering_type !== US_CRIMINAL_ORDERING_TYPE.CURRENT_ADDRESS
  );
}

function hasSevenYearCourt(
  checks: UsCriminalChecks,
  courtCheckType: UsCriminalCourtCheckType
): boolean {
  return courtCheckType in checks && isSevenYearCourt(checks[courtCheckType]);
}

export function templateUsesSevenYearCourt(
  preference: string | undefined,
  checks: UsCriminalChecks | undefined
): boolean {
  if (preference === US_CRIMINAL_PREFERENCE.TIER_3) {
    return true;
  }
  if (preference !== US_CRIMINAL_PREFERENCE.CUSTOM) {
    return false;
  }
  const usChecks = checks ?? {};
  return (
    hasSevenYearCourt(usChecks, US_CRIMINAL_CHECK_TYPE.FEDERAL) ||
    hasSevenYearCourt(usChecks, US_CRIMINAL_CHECK_TYPE.COUNTY)
  );
}

export function lookbackYearsMinimum(
  preference: string | undefined,
  checks: UsCriminalChecks | undefined
): number {
  return templateUsesSevenYearCourt(preference, checks)
    ? CRIMINAL_RECORD_VALIDATION.SEVEN_YEAR_LOOKBACK_YEARS
    : CRIMINAL_RECORD_VALIDATION.MIN_LOOKBACK_YEARS;
}

export function coerceCurrentAddressUsPreference(
  preference: UsCriminalPreferenceEnum,
  checks: UsCriminalChecks
): {
  us_criminal_preference: UsCriminalPreferenceEnum;
  us_criminal_checks: UsCriminalChecks;
} {
  if (
    preference !== US_CRIMINAL_PREFERENCE.TIER_3 &&
    preference !== US_CRIMINAL_PREFERENCE.CUSTOM
  ) {
    return {
      us_criminal_preference: preference,
      us_criminal_checks: checks,
    };
  }
  return {
    us_criminal_preference: US_CRIMINAL_PREFERENCE.TIER_2,
    us_criminal_checks: cloneUsCriminalChecks(US_CRIMINAL_TIER_CHECKS.TIER_2),
  };
}

function hasUsCriminalAddonHost(checks: UsCriminalChecks): boolean {
  return (
    US_CRIMINAL_CHECK_TYPE.NCD in checks ||
    US_CRIMINAL_CHECK_TYPE.FEDERAL in checks ||
    US_CRIMINAL_CHECK_TYPE.COUNTY in checks ||
    US_CRIMINAL_CHECK_TYPE.NSOR in checks
  );
}

function usCriminalSsnCouplingFlags(checks: UsCriminalChecks): {
  hasNcd: boolean;
  ssnRequired: boolean;
  ssnForbidden: boolean;
} {
  const hasNcd = US_CRIMINAL_CHECK_TYPE.NCD in checks;
  return {
    hasNcd,
    ssnRequired:
      hasNcd ||
      hasSevenYearCourt(checks, US_CRIMINAL_CHECK_TYPE.FEDERAL) ||
      hasSevenYearCourt(checks, US_CRIMINAL_CHECK_TYPE.COUNTY),
    ssnForbidden: !hasUsCriminalAddonHost(checks),
  };
}

function applyUsCriminalSsnCouplingInPlace(checks: UsCriminalChecks): void {
  const { ssnRequired, ssnForbidden } = usCriminalSsnCouplingFlags(checks);
  if (ssnForbidden) {
    delete checks[US_CRIMINAL_CHECK_TYPE.SSN];
  } else if (ssnRequired) {
    checks[US_CRIMINAL_CHECK_TYPE.SSN] = {};
  }
  if (!hasUsCriminalAddonHost(checks)) {
    delete checks[US_CRIMINAL_CHECK_TYPE.GWL];
  }
}

export function applyUsCriminalSsnCoupling(
  checks: UsCriminalChecks
): UsCriminalChecks {
  const next = cloneUsCriminalChecks(checks);
  applyUsCriminalSsnCouplingInPlace(next);
  return next;
}

export type UsCriminalCourtVariant = "current" | "seven_year";

type UsCriminalCustomCheckboxSpec =
  | string
  | { court: UsCriminalCourtCheckType; variant: UsCriminalCourtVariant };

const US_CRIMINAL_CUSTOM_CHECKBOX_SPEC: Record<
  UsCriminalCustomCheckbox,
  UsCriminalCustomCheckboxSpec
> = {
  [US_CRIMINAL_CUSTOM_CHECKBOX.SSN]: US_CRIMINAL_CHECK_TYPE.SSN,
  [US_CRIMINAL_CUSTOM_CHECKBOX.NCD]: US_CRIMINAL_CHECK_TYPE.NCD,
  [US_CRIMINAL_CUSTOM_CHECKBOX.NSOR]: US_CRIMINAL_CHECK_TYPE.NSOR,
  [US_CRIMINAL_CUSTOM_CHECKBOX.GWL]: US_CRIMINAL_CHECK_TYPE.GWL,
  [US_CRIMINAL_CUSTOM_CHECKBOX.FEDERAL_CURRENT]: {
    court: US_CRIMINAL_CHECK_TYPE.FEDERAL,
    variant: "current",
  },
  [US_CRIMINAL_CUSTOM_CHECKBOX.COUNTY_CURRENT]: {
    court: US_CRIMINAL_CHECK_TYPE.COUNTY,
    variant: "current",
  },
  [US_CRIMINAL_CUSTOM_CHECKBOX.FEDERAL_SEVEN_YEAR]: {
    court: US_CRIMINAL_CHECK_TYPE.FEDERAL,
    variant: "seven_year",
  },
  [US_CRIMINAL_CUSTOM_CHECKBOX.COUNTY_SEVEN_YEAR]: {
    court: US_CRIMINAL_CHECK_TYPE.COUNTY,
    variant: "seven_year",
  },
};

function setCourtVariant(
  checks: UsCriminalChecks,
  courtCheckType: UsCriminalCourtCheckType,
  variant: UsCriminalCourtVariant | null
): void {
  if (variant === "current") {
    checks[courtCheckType] = { ...CURRENT_ADDRESS_COURT_ARGUMENTS };
  } else if (variant === "seven_year") {
    checks[courtCheckType] = { ...SEVEN_YEAR_COURT_ARGUMENTS };
  } else {
    delete checks[courtCheckType];
  }
}

function setSimpleCheck(
  checks: UsCriminalChecks,
  checkType: string,
  selected: boolean
): void {
  if (selected) {
    checks[checkType] = {};
  } else {
    delete checks[checkType];
  }
}

export function toggleUsCriminalCustomCheckbox(
  checks: UsCriminalChecks,
  checkbox: UsCriminalCustomCheckbox,
  selected: boolean
): UsCriminalChecks {
  const next = cloneUsCriminalChecks(checks);
  const spec = US_CRIMINAL_CUSTOM_CHECKBOX_SPEC[checkbox];

  if (typeof spec === "string") {
    setSimpleCheck(next, spec, selected);
  } else {
    setCourtVariant(next, spec.court, selected ? spec.variant : null);
  }

  applyUsCriminalSsnCouplingInPlace(next);
  return next;
}

export function usCriminalSsnCheckboxState(checks: UsCriminalChecks): {
  isSelected: boolean;
  isDisabled: boolean;
} {
  const { ssnRequired, ssnForbidden } = usCriminalSsnCouplingFlags(checks);

  return {
    isSelected: US_CRIMINAL_CHECK_TYPE.SSN in checks,
    isDisabled: ssnForbidden || ssnRequired,
  };
}

export function usCriminalGwlCheckboxState(checks: UsCriminalChecks): {
  isSelected: boolean;
  isDisabled: boolean;
} {
  return {
    isSelected: US_CRIMINAL_CHECK_TYPE.GWL in checks,
    isDisabled: !hasUsCriminalAddonHost(checks),
  };
}

export function isUsCriminalCustomCheckboxSelected(
  checks: UsCriminalChecks,
  checkbox: UsCriminalCustomCheckbox
): boolean {
  const spec = US_CRIMINAL_CUSTOM_CHECKBOX_SPEC[checkbox];
  if (typeof spec === "string") {
    return spec in checks;
  }
  if (spec.variant === "current") {
    return spec.court in checks && isCurrentAddressCourt(checks[spec.court]);
  }
  return hasSevenYearCourt(checks, spec.court);
}

export function australianOptionToCheckArguments(
  option: AustralianCriminalOption
): AustralianCriminalCheckArguments {
  if (option === AUSTRALIAN_CRIMINAL_OPTION.STANDARD_NCCHC) {
    return {
      australian_criminal_preference: "NCCHC",
      australian_ncchc_check_type: "STANDARD_AUSTRALIAN_NCCHC_1",
    };
  }
  if (option === AUSTRALIAN_CRIMINAL_OPTION.VOLUNTEER_NCCHC) {
    return {
      australian_criminal_preference: "NCCHC",
      australian_ncchc_check_type: "VOLUNTEER_AUSTRALIAN_NCCHC_1",
    };
  }
  return {
    australian_criminal_preference: "AFP",
    australian_ncchc_check_type: "STANDARD_AUSTRALIAN_NCCHC_1",
  };
}

export function usPreferenceToCheckArguments(
  preference: UsCriminalPreferenceEnum,
  customChecks?: UsCriminalChecks | null
): UsCriminalPreferenceFields {
  if (preference === US_CRIMINAL_PREFERENCE.CUSTOM) {
    return {
      us_criminal_preference: US_CRIMINAL_PREFERENCE.CUSTOM,
      us_criminal_checks: applyUsCriminalSsnCoupling(customChecks ?? {}),
    };
  }

  if (preference in US_CRIMINAL_TIER_CHECKS) {
    const tier = preference as UsCriminalTierPreference;
    return {
      us_criminal_preference: tier,
      us_criminal_checks: cloneUsCriminalChecks(US_CRIMINAL_TIER_CHECKS[tier]),
    };
  }

  return {
    us_criminal_preference: US_CRIMINAL_PREFERENCE.NONE,
    us_criminal_checks: {},
  };
}

export function checkArgumentsToUsPreference(
  args: {
    us_criminal_preference?: UsCriminalPreferenceEnum | string;
    us_criminal_checks?: UsCriminalChecks | null;
  } | null
): UsCriminalPreferenceEnum {
  const preference = args?.us_criminal_preference;
  if (
    preference === US_CRIMINAL_PREFERENCE.NONE ||
    preference === US_CRIMINAL_PREFERENCE.TIER_1 ||
    preference === US_CRIMINAL_PREFERENCE.TIER_2 ||
    preference === US_CRIMINAL_PREFERENCE.TIER_3 ||
    preference === US_CRIMINAL_PREFERENCE.CUSTOM
  ) {
    return preference;
  }
  return US_CRIMINAL_PREFERENCE.NONE;
}

export function canUpdateCheckArguments({
  criminalTemplatesEnhancements,
  isInUse,
}: {
  criminalTemplatesEnhancements: boolean;
  isInUse: boolean;
}): boolean {
  return criminalTemplatesEnhancements && !isInUse;
}

export function criminalRecordTemplateSchema() {
  return object().shape({
    name: string().required().label(t("Template name")),
    lookback_years: number()
      .required()
      .label(t("Look back period years"))
      .max(CRIMINAL_RECORD_VALIDATION.MAX_LOOKBACK_YEARS)
      .when(["us_criminal_preference", "us_criminal_checks"], {
        is: (
          preference: string | undefined,
          checks: UsCriminalChecks | undefined
        ) => templateUsesSevenYearCourt(preference, checks),
        then: (schema) =>
          schema.min(CRIMINAL_RECORD_VALIDATION.SEVEN_YEAR_LOOKBACK_YEARS),
        otherwise: (schema) =>
          schema.min(CRIMINAL_RECORD_VALIDATION.MIN_LOOKBACK_YEARS),
      }),
    check_arguments: object().shape({
      umbrella_minimum_address_duration_months: number().required(),
      canadian_criminal_preference: string().required(),
    }),
    us_criminal_preference: string(),
    us_criminal_checks: object().when("us_criminal_preference", {
      is: US_CRIMINAL_PREFERENCE.CUSTOM,
      then: (schema) =>
        schema.test(
          "us-criminal-custom-not-empty",
          t("Select at least one US criminal check."),
          (value) => Object.keys(value ?? {}).length > 0
        ),
    }),
  });
}

export function nameOnlyCriminalRecordTemplateSchema() {
  return object().shape({
    name: string().required().label(t("Template name")),
  });
}

export function checkArgumentsToAustralianOption(
  args: Partial<AustralianCriminalCheckArguments> | null
): AustralianCriminalOption {
  if (args?.australian_criminal_preference !== "NCCHC") {
    return AUSTRALIAN_CRIMINAL_OPTION.AFP;
  }
  if (args.australian_ncchc_check_type === "VOLUNTEER_AUSTRALIAN_NCCHC_1") {
    return AUSTRALIAN_CRIMINAL_OPTION.VOLUNTEER_NCCHC;
  }
  return AUSTRALIAN_CRIMINAL_OPTION.STANDARD_NCCHC;
}

/**
 * Formats maximum prices for display by showing numeric values with currency codes.
 * Strips currency symbols to avoid redundancy (e.g., "A$12 AUD" becomes "12 AUD").
 */
export function formatMaximumPrices(
  maximumPrice: Record<string, number | undefined> | null | undefined
): string {
  if (!maximumPrice || Object.keys(maximumPrice).length === 0) {
    return "";
  }

  return Object.entries(maximumPrice)
    .filter(([, amount]) => amount !== undefined)
    .map(([currency, amount]) => {
      const formattedAmount = formatCurrency(amount!, undefined, currency, 0);
      const numericValue = formattedAmount.replace(/[^\d,]/g, "");
      return `${numericValue} ${currency}`;
    })
    .join(", ");
}
